import React, { useState, useEffect } from 'react';
import {
  Button,
  Grid,
  TextField,
  Autocomplete,
  Chip
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import MainCard from 'ui-component/cards/MainCard';
import LoadingBackdrop from 'ui-component/full-screen-loadder/LoadingBackdrop';
import { get, post, put } from '../axios/apiService';
import toast from 'react-hot-toast';
import { isEmpty, validateEmail } from 'utils/validator';
import PhoneInput from 'react-phone-input-2';
import parsePhoneNumberFromString from 'libphonenumber-js';
import 'react-phone-input-2/lib/style.css';
import { usePermissions } from 'hooks/usePermissions';
import { MODULE_KEYS } from 'store/constant';

function AddEditUserForm() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [isLoading, setLoading] = useState(false);
  const [roles, setRoles] = useState([]);
  const [user, setUser] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    roleIds: []
  });
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  const [isValidation, setIsValidation] = useState(false);
  const { hasWritePermission } = usePermissions();
  const canEdit = hasWritePermission(MODULE_KEYS.User_Management)

  useEffect(() => {
    const fetchRoles = async () => {
      setLoading(true);
      try {
        const response = await get('AdminRoles?$top=600', null);
        if (response?.values) {
          setRoles(response.values);
        }
      } catch (err) {
        console.error('Failed to load roles', err);
        toast.error(err?.response?.data || 'Failed to load roles');
      } finally {
        setLoading(false);
      }
    };

    fetchRoles();
  }, []);

  useEffect(() => {
    if (id != null && id != undefined) {
      getByIdUser();
    }
  }, [id]);

  useEffect(() => {
    if (isValidation) {
      validator();
    }
  }, [user.firstName, user.lastName, user.email, user.phoneNumber, user.roleIds]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setUser((prevUser) => ({
      ...prevUser,
      [name]: value
    }));
  };

  const handlePhoneChange = (value) => {
    setUser((prevUser) => ({
      ...prevUser,
      phoneNumber: value
    }));
  };

  const handleRoleChange = (event, newValue) => {
    const roleIds = newValue.map(role => role.id);
    setSelectedRoles(newValue);
    setUser((prevUser) => ({
      ...prevUser,
      roleIds: roleIds
    }));
  };

  const validator = () => {
    const errors = {};

    if (isEmpty(user?.firstName)) {
      errors.firstName = 'Please enter first name';
    }

    if (isEmpty(user?.lastName)) {
      errors.lastName = 'Please enter last name';
    }

    if (isEmpty(user?.email)) {
      errors.email = 'Please enter email';
    } else if (!validateEmail(user.email)) {
      errors.email = 'Please enter a valid email';
    }

    if (isEmpty(user?.phoneNumber)) {
      errors.phoneNumber = 'Please enter phone number';
    } else {
      const mobileNumber = user.phoneNumber.startsWith('+') ? user.phoneNumber : `+${user.phoneNumber}`;
      const phoneNumber = parsePhoneNumberFromString(mobileNumber);
      if (!phoneNumber || !phoneNumber.isValid()) {
        errors.phoneNumber = 'Invalid phone number';
      }
    }

    if (!user.roleIds || user.roleIds.length === 0) {
      errors.roleIds = 'Please select at least one role';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    setIsValidation(true);
    if (!validator()) return;

    const apiUrl = id != undefined && id != null ? `AdminUsers/${id}` : 'AdminUsers';

    try {
      setLoading(true);

      const param = {
        firstName: user.firstName,
        lastName: user.lastName,
        phoneNumber: user.phoneNumber,
        roleIds: user.roleIds
      };

      if (id) {
        param.id = id;
      } else {
        param.email = user.email;
      }

      const response = id
        ? await put(apiUrl, param)
        : await post(apiUrl, param);

      if (response != null) {
        toast.success(id ? 'User updated successfully' : 'User added successfully');
        navigate('/admin-user-management');
      }
    } catch (error) {
      console.error('Submit error:', error);
      toast.error(error?.response?.data || 'Failed to save user');
    } finally {
      setLoading(false);
    }
  };

  const getByIdUser = async () => {
    setLoading(true);
    try {
      const response = await get(`AdminUsers/${id}`, null);
      if (response != null) {
        setUser({
          firstName: response?.firstName || '',
          lastName: response?.lastName || '',
          email: response?.email || '',
          phoneNumber: response?.phoneNumber || '',
          roleIds: response?.adminUserRoles?.map(x => x.adminRoleId) || []
        });

        // Set selected roles for autocomplete
        const userRoleIds = response?.adminUserRoles?.map(x => x.adminRoleId) || [];
        const userRoles = roles.filter(role => userRoleIds.includes(role.id));
        setSelectedRoles(userRoles);
      }
    } catch (err) {
      console.error(err.message);
      toast.error(err?.response?.data || 'Failed to load user');
      navigate('/admin-user-management');
    } finally {
      setLoading(false);
    }
  };

  // Update selected roles when roles are loaded and user data is available
  useEffect(() => {
    if (roles.length > 0 && user.roleIds.length > 0) {
      const userRoles = roles.filter(role => user.roleIds.includes(role.id));
      setSelectedRoles(userRoles);
    }
  }, [roles, user.roleIds]);

  return (
    <div>
      <MainCard
        title={id ? canEdit ? 'Edit Admin User' : "View Admin User"  : 'Add Admin User'}
        backButton={{ onClick: () => navigate('/admin-user-management') }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4} lg={4}>
            <TextField
              fullWidth
              label="First Name *"
              name="firstName"
              type="text"
              value={user.firstName}
              onChange={handleChange}
              disabled={!canEdit}
            />
            <div style={{ color: 'red' }}>{formErrors.firstName}</div>
          </Grid>

          <Grid item xs={12} sm={6} md={4} lg={4}>
            <TextField
              fullWidth
              label="Last Name *"
              name="lastName"
              type="text"
              value={user.lastName}
              onChange={handleChange}
              disabled={!canEdit}
            />
            <div style={{ color: 'red' }}>{formErrors.lastName}</div>
          </Grid>

          <Grid item xs={12} sm={6} md={4} lg={4}>
            <TextField
              fullWidth
              label="Email *"
              name="email"
              type="email"
              value={user.email}
              onChange={handleChange}
              disabled={(id != null && id != undefined) || !canEdit}
            />
            <div style={{ color: 'red' }}>{formErrors.email}</div>
          </Grid>

          <Grid item xs={12} sm={6} md={4} lg={4}>
            <PhoneInput
              country={'in'}
              value={user.phoneNumber}
              onChange={handlePhoneChange}
              inputStyle={{
                width: '100%',
                height: '56px',
                borderRadius: '4px',
              }}
              disabled={!canEdit}
            />
            <div style={{ color: 'red' }}>{formErrors.phoneNumber}</div>
          </Grid>

          <Grid item xs={12} sm={6} md={8} lg={8}>
            <Autocomplete
              multiple
              options={roles}
              getOptionLabel={(option) => option.name}
              value={selectedRoles}
              onChange={handleRoleChange}
              renderInput={(params) => (
                <TextField {...params} label="Roles *" />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option.name}
                    {...getTagProps({ index })}
                    key={option.id}
                  />
                ))
              }
              disabled={!canEdit}
            />
            <div style={{ color: 'red' }}>{formErrors.roleIds}</div>
          </Grid>
        </Grid>
        {
          canEdit && (
            <Button
              variant="contained"
              sx={{ marginTop: '20px', padding: '5px 30px' }}
              onClick={handleSubmit}
              disabled={!canEdit}
            >
              {id ? 'Update' : 'Save'}
            </Button>
          )
        }
      </MainCard>
      <LoadingBackdrop open={isLoading} />
    </div>
  );
}

export default AddEditUserForm;